import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CONFLICT_DEFAULTS } from '@pirouette/shared';
import { useDancerProductionSchedule, type DancerScheduleRow } from '@/data/conflicts';
import { supabase } from '@/lib/supabase';
import { Empty } from '@/components/ui/Empty';
import { Badge } from '@/components/ui/Badge';

export const Route = createFileRoute('/admin/productions/$id/by-dancer')({
  component: ByDancer,
});

// Canonical threshold from the shared module — keeps this view aligned
// with detectLineupConflicts' default.
const QUICK_CHANGE_THRESHOLD_SECONDS = CONFLICT_DEFAULTS.quickChangeMinSeconds;

interface DancerInfo {
  id: string;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  family_id: string;
}

function useDancersByIds(ids: string[]) {
  return useQuery({
    enabled: ids.length > 0,
    queryKey: ['dancers-batch', [...ids].sort().join(',')],
    queryFn: async (): Promise<DancerInfo[]> => {
      const { data, error } = await supabase
        .from('dancers')
        .select('id, first_name, last_name, preferred_name, family_id')
        .in('id', ids);
      if (error) throw error;
      return data ?? [];
    },
  });
}

function ByDancer() {
  const { id: productionId } = Route.useParams();
  const schedule = useDancerProductionSchedule(productionId);
  const dancerIds = useMemo(
    () => [...new Set((schedule.data ?? []).map((r) => r.dancer_id))],
    [schedule.data],
  );
  const dancers = useDancersByIds(dancerIds);

  const dancerById = useMemo(
    () => new Map((dancers.data ?? []).map((d) => [d.id, d])),
    [dancers.data],
  );

  // Group schedule rows by dancer.
  const byDancer = useMemo(() => {
    const m = new Map<string, DancerScheduleRow[]>();
    for (const r of schedule.data ?? []) {
      const list = m.get(r.dancer_id) ?? [];
      list.push(r);
      m.set(r.dancer_id, list);
    }
    return m;
  }, [schedule.data]);

  // Sort dancers by last_name, first_name.
  const sortedDancerIds = useMemo(() => {
    return [...byDancer.keys()].sort((a, b) => {
      const da = dancerById.get(a);
      const db = dancerById.get(b);
      const aKey = `${da?.last_name ?? ''} ${da?.first_name ?? ''}`;
      const bKey = `${db?.last_name ?? ''} ${db?.first_name ?? ''}`;
      return aKey.localeCompare(bKey);
    });
  }, [byDancer, dancerById]);

  return (
    <div className="space-y-8">
      <header>
        <Link
          to="/admin/productions/$id"
          params={{ id: productionId }}
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Production
        </Link>
        <h1 className="mt-2">By dancer</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Every dancer in this production with their per-show appearances and quick-change gaps
          flagged.
        </p>
      </header>

      {schedule.isLoading && <p aria-live="polite">Loading…</p>}

      {sortedDancerIds.length === 0 && !schedule.isLoading && (
        <Empty
          title="No cast yet"
          description="Once you assign dancers to dances in this production, they'll appear here."
        />
      )}

      {sortedDancerIds.length > 0 && (
        <ul className="grid gap-4">
          {sortedDancerIds.map((dancerId) => {
            const d = dancerById.get(dancerId);
            const appearances = byDancer.get(dancerId) ?? [];
            // Sort by show's performance_at (chronological) then sequence
            // — UUID-based ordering would render Saturday/Sunday in
            // arbitrary order and confuse gap computations.
            const sorted = [...appearances].sort(
              (a, b) =>
                a.performance_at.localeCompare(b.performance_at) ||
                a.sequence_index - b.sequence_index,
            );
            return (
              <li key={dancerId} className="surface-elevated p-5">
                <h2 className="font-[var(--font-display)] text-[var(--text-xl)]">
                  {d ? (d.preferred_name ?? d.first_name) : 'Unknown dancer'} {d?.last_name ?? ''}
                </h2>
                <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  {appearances.length} {appearances.length === 1 ? 'appearance' : 'appearances'}{' '}
                  across this production
                </p>

                <ul className="mt-4 grid gap-2">
                  {sorted.map((row, i) => {
                    const prev = sorted[i - 1];
                    const sameShow = prev?.show_id === row.show_id;
                    let gapSec: number | null = null;
                    if (sameShow && prev.computed_start_time && row.computed_start_time) {
                      const prevEnd =
                        new Date(prev.computed_start_time).getTime() + prev.length_seconds * 1000;
                      const thisStart = new Date(row.computed_start_time).getTime();
                      gapSec = Math.round((thisStart - prevEnd) / 1000);
                    }
                    const quick = gapSec !== null && gapSec < QUICK_CHANGE_THRESHOLD_SECONDS;
                    return (
                      <li
                        key={`${row.show_id}-${row.dance_id}`}
                        className={`surface flex flex-wrap items-baseline justify-between gap-3 p-3 ${
                          quick ? 'ring-1 ring-[var(--color-warn)]' : ''
                        }`}
                      >
                        <div>
                          <p className="font-medium">{row.dance_name}</p>
                          <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                            {row.show_name} ·{' '}
                            {row.computed_start_time
                              ? new Date(row.computed_start_time).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '—'}
                          </p>
                        </div>
                        {gapSec !== null && (
                          <Badge tone={quick ? 'warn' : 'neutral'}>
                            {quick ? '⚠ ' : ''}gap {Math.floor(gapSec / 60)}m{' '}
                            {String(gapSec % 60).padStart(2, '0')}s
                          </Badge>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
